import { styled } from '@mui/material/styles';
import { useAtomValue } from 'jotai';
import { ReactElement, ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router';

import { PortfolioCurrencyToggle } from '../../../components/CurrencyToggle';
import { GenericFallbacksWrapper } from '../../../components/Fallback/GenericFallbacksWrapper';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { readsSelectedCurrency } from '../../../hooks/usePortfolioCurrencyScope';
import { Breakpoint, ContainerName, MaxHeaderHeightInRems } from '../../../constants/styles';
import { colors } from '../../../theme/colors';
import { ViewTypeByData } from '../../../data-models/view-config.data-model';
import { PortfolioDatePicker } from '../../../pages/PortfolioOverview/components/ViewNavigation/components/PortfolioDatePicker';
import { isAsOfDateLoadingPFJ } from '../../../pages/PortfolioOverview/state/UIStateJ';
import {
  selectedViewIdPFJ,
  selectedViewPFJ,
  selectedViewPFLoadableJ,
  selectedViewSummaryPFJ,
} from '../../../pages/PortfolioOverview/state/ViewStateJ';
import { PermissionService } from '../../../services/PermissionService';
import { ChatBotButton } from '../../ChatBotSection/ChatBotSection';
import { LoadingBar } from '../../LoadingBar/LoadingBar';
import { FundFilterPF2 } from '../Navbar/Navigation/PortfolioReporting/FundNavigation/FundFilterPF2';
import { FundFilterSingleSelect } from '../Navbar/Navigation/PortfolioReporting/FundNavigation/FundFilterSingleSelect';
import { Breadcrumb as Breadcrumbs } from './Breadcrumbs';
import { useBreadCrumbs } from './useBreadCrumbs';

const Wrapper = styled('header')`
  display: grid;
  grid-auto-flow: column;
  column-gap: 1rem;
  width: 100%;
  &.portfolio {
    grid-auto-columns: auto 1fr auto auto auto min-content;
  }
  @container ${ContainerName.headerContainer} (max-width: ${Breakpoint.hideBreadcrumbs}) {
    &.portfolio {
      grid-auto-columns: 1fr auto auto;
      .MuiBreadcrumbs-root {
        display: none;
      }
    }
    column-gap: 0.5rem;
  }
  align-items: center;
  justify-content: stretch;
  padding: 0.5rem 1.5rem;
  min-height: 65px;
  width: 100%;
  background: #fafbfe;
  max-height: ${MaxHeaderHeightInRems}rem;

  /*
   * Entity Management reads as its own bar: white against the tinted surfaces below it,
   * with a rule marking where the header stops and the module's toolbar starts.
   *
   * The columns are pinned too. Spare width is otherwise shared out by
   * \`justify-content: stretch\`, which leaves the actions stranded mid-row with a 40px
   * chat button holding a 245px column beside them; giving the slack to the breadcrumb
   * carries them over to it.
   */
  &.entities {
    background: #ffffff;
    border-bottom: 1px solid ${colors.neutral[10]};
    grid-auto-columns: 1fr auto auto;
  }

  header {
    width: 100%;
  }
`;

const LoadingBarWrapper = styled('div')`
  position: sticky;
  top: 0;
`;

interface IHeaderProps {
  children?: ReactNode | null;
}

export const viewTypesWithoutFundFilter = new Set([
  ViewTypeByData.people,
  ViewTypeByData.security,
  ViewTypeByData.multiperiod,
]);

const viewTypesWithSingleFundFilter = new Set([ViewTypeByData.multiperiod]);

export function Header({ children = null }: IHeaderProps): ReactElement {
  const breadCrumbs = useBreadCrumbs();
  const { pathname } = useLocation();
  const isPortfolio = useMemo(() => pathname.startsWith(`/${ROUTES.PORTFOLIO}`), [pathname]);
  const isSourcing = useMemo(() => pathname.startsWith(`/${ROUTES.SOURCING}`), [pathname]);
  const isEntities = useMemo(() => pathname.startsWith(`/${ROUTES.ENTITIES}`), [pathname]);
  const viewType = useAtomValue(selectedViewSummaryPFJ)?.viewType;
  const selectedViewState = useAtomValue(selectedViewPFLoadableJ).state;
  const currentView = useAtomValue(selectedViewIdPFJ);
  const isAsOfDateLoading = useAtomValue(isAsOfDateLoadingPFJ);
  const isAuditView = PermissionService.get().isAuditor();

  const showBreadcrumbs = !isPortfolio && !isSourcing;

  return (
    <>
      <Wrapper data-testid={'header'} className={isPortfolio ? 'portfolio' : isEntities ? 'entities' : ''}>
        {showBreadcrumbs ? <Breadcrumbs breadcrumbs={breadCrumbs} /> : <div /> /* layout placeholder */}
        {children}
        {isPortfolio && selectedViewState !== 'hasError' && (
          // These controls read the opened view's settings. They wait for them here, not the header and
          // the page, and a view that fails to load leaves them out (Portfolio says why) instead of the page.
          <GenericFallbacksWrapper suspenseFallback={null} errorFallback={<></>}>
            <SelectedViewCurrencyToggle />
            {!isAuditView && <PortfolioDatePicker key={currentView} isLoading={isAsOfDateLoading} />}
            {!isAuditView && viewType != null && !viewTypesWithoutFundFilter.has(viewType) && (
              <FundFilterPF2 />
            )}
            {!isAuditView && viewType != null && viewTypesWithSingleFundFilter.has(viewType) && (
              <FundFilterSingleSelect />
            )}
          </GenericFallbacksWrapper>
        )}
        {!isAuditView ? <ChatBotButton /> : <div /> /* layout placeholder */}
      </Wrapper>
      <LoadingBarWrapper>
        <LoadingBar />
      </LoadingBarWrapper>
    </>
  );
}

// The toggle itself hides on a USD-only tenant.
function SelectedViewCurrencyToggle() {
  const selectedView = useAtomValue(selectedViewPFJ);
  return readsSelectedCurrency(selectedView) ? <PortfolioCurrencyToggle /> : null;
}
